import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Card,
	CardContent,
	TextField,
	Button,
	Avatar,
	Grid,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Alert,
	Skeleton,
	Stack,
	CircularProgress,
	Tooltip,
	Autocomplete,
} from "@mui/material";
import { Upload, Camera, Info } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";

const AVATAR_CACHE_KEY = "userAvatarCache";

const Account = () => {
	const { user } = useAuth();
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState({ type: "", text: "" });
	const [gradesList, setGradesList] = useState([]);
	const [schoolSaved, setSchoolSaved] = useState(false);

	// Form state
	const [profile, setProfile] = useState({
		full_name: "",
		phone_number: "",
		email: "",
		avatar_url: null,
		school_id: "",
	});

	// Data for dropdowns
	const [schools, setSchools] = useState([]);
	const [grades, setGrades] = useState([]);
	const [selectedGrades, setSelectedGrades] = useState([]);

	// Load cached avatar on mount
	useEffect(() => {
		const cachedAvatar = localStorage.getItem(AVATAR_CACHE_KEY);
		if (cachedAvatar) {
			setProfile((prev) => ({ ...prev, avatar_url: cachedAvatar }));
		}
	}, []);

	useEffect(() => {
		fetchUserProfile();
		fetchSchools();
		fetchAllGrades();
	}, [user]);

	const fetchAllGrades = async () => {
		try {
			const { data, error } = await supabase
				.from("grades")
				.select("id, name")
				.order("id", { ascending: true });

			if (error) throw error;
			setGradesList(data || []);
		} catch (error) {
			console.error("Error fetching grades:", error);
		}
	};

	// Add this function to fetch public URL directly
	const downloadAvatar = async (avatarUrl) => {
		try {
			// Extract file path from the full URL
			const urlParts = avatarUrl.split("/public/avatars/");
			if (urlParts.length < 2) return null;

			const filePath = urlParts[1];

			// Get the signed URL directly from Supabase
			const { data, error } = await supabase.storage
				.from("avatars")
				.createSignedUrl(filePath, 60 * 60); // Valid for 1 hour

			if (error) {
				console.error("Error getting signed URL:", error);
				return null;
			}

			return data.signedUrl;
		} catch (error) {
			console.error("Error downloading avatar:", error);
			return null;
		}
	};

	const fetchUserProfile = async () => {
		try {
			if (!user) return;

			const { data: profileData, error: profileError } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", user.id)
				.single();

			if (profileError) throw profileError;

			const { data: teacherGrades, error: gradesError } = await supabase
				.from("teacher_grades")
				.select("grade_id")
				.eq("profile_id", user.id);

			if (gradesError) throw gradesError;

			if (profileData) {
				// Get a fresh signed URL if we have an avatar
				let avatarUrl = profileData.avatar_url;
				if (avatarUrl) {
					const signedUrl = await downloadAvatar(avatarUrl);
					if (signedUrl) {
						avatarUrl = signedUrl;
					}
				}

				// Set schoolSaved to true if the user already has a school assigned
				setSchoolSaved(!!profileData.school_id);

				setProfile({
					full_name: profileData.full_name || "",
					phone_number: profileData.phone_number || "",
					email: profileData.email || user.email,
					avatar_url: avatarUrl,
					school_id: profileData.school_id || "",
				});

				setSelectedGrades(teacherGrades?.map((tg) => tg.grade_id) || []);

				if (profileData.school_id) {
					fetchGrades(profileData.school_id);
				}
			}
		} catch (error) {
			console.error("Error fetching profile:", error);
			setMessage({ type: "error", text: "Error loading profile" });
		} finally {
			setLoading(false);
		}
	};

	const fetchSchools = async () => {
		try {
			const { data, error } = await supabase.from("schools").select("id, name");

			if (error) throw error;
			setSchools(data || []);
		} catch (error) {
			console.error("Error fetching schools:", error);
		}
	};

	const fetchGrades = async (schoolId) => {
		try {
			const { data, error } = await supabase
				.from("school_grades")
				.select("grade_id")
				.eq("school_id", schoolId);

			if (error) throw error;
			setGrades(data.map((g) => g.grade_id) || []);
		} catch (error) {
			console.error("Error fetching grades:", error);
		}
	};

	const handleSchoolChange = (schoolId) => {
		setProfile((prev) => ({ ...prev, school_id: schoolId }));
		setSelectedGrades([]);
		fetchGrades(schoolId);
	};

	// Also modify the handleAvatarUpload function to get the signed URL after upload
	const handleAvatarUpload = async (event) => {
		try {
			const file = event.target.files?.[0];
			if (!file) return;

			setUploading(true);
			const fileExt = file.name.split(".").pop();
			const fileName = `${Date.now()}.${fileExt}`;

			const { data, error: uploadError } = await supabase.storage
				.from("avatars")
				.upload(fileName, file, {
					upsert: true,
				});

			if (uploadError) throw uploadError;

			// Get signed URL immediately after upload
			const { data: signedUrlData, error: signedUrlError } =
				await supabase.storage
					.from("avatars")
					.createSignedUrl(fileName, 60 * 60); // Valid for 1 hour

			if (signedUrlError) throw signedUrlError;

			const publicUrl = signedUrlData.signedUrl;

			localStorage.setItem(AVATAR_CACHE_KEY, publicUrl);
			setProfile((prev) => ({ ...prev, avatar_url: publicUrl }));
			setMessage({
				type: "success",
				text: "Profile photo uploaded successfully. Save changes to update your profile.",
			});
		} catch (error) {
			console.error("Error uploading avatar:", error);
			setMessage({ type: "error", text: "Error uploading profile photo" });
		} finally {
			setUploading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setMessage({ type: "", text: "" });

		try {
			// Update profile
			const { error: profileError } = await supabase
				.from("profiles")
				.update({
					full_name: profile.full_name,
					phone_number: profile.phone_number,
					avatar_url: profile.avatar_url,
					school_id: profile.school_id,
					updated_at: new Date().toISOString(),
				})
				.eq("id", user.id);

			if (profileError) throw profileError;

			// Delete existing teacher grade associations
			const { error: deleteError } = await supabase
				.from("teacher_grades")
				.delete()
				.eq("profile_id", user.id);

			if (deleteError) throw deleteError;

			// Insert new teacher grade associations
			if (selectedGrades.length > 0) {
				const gradeData = selectedGrades.map((gradeId) => ({
					profile_id: user.id,
					grade_id: gradeId,
					school_id: profile.school_id,
				}));

				const { error: gradesError } = await supabase
					.from("teacher_grades")
					.insert(gradeData);

				if (gradesError) throw gradesError;
			}

			// If this is the first time setting a school, mark it as saved
			if (profile.school_id && !schoolSaved) {
				setSchoolSaved(true);
			}

			setMessage({ type: "success", text: "Profile updated successfully" });
		} catch (error) {
			console.error("Error updating profile:", error);
			setMessage({ type: "error", text: "Error updating profile" });
		} finally {
			setSaving(false);
		}
	};

	const availableGradeOptions = useMemo(
		() =>
			grades
				.map((gradeId) => {
					const grade = gradesList.find((g) => g.id === gradeId);
					return {
						id: gradeId,
						name: grade ? grade.name : `Grade ${gradeId}`,
					};
				})
				.sort((a, b) =>
					a.name.localeCompare(b.name, undefined, { numeric: true })
				),
		[grades, gradesList]
	);

	const selectedGradeOptions = useMemo(
		() =>
			availableGradeOptions.filter((grade) =>
				selectedGrades.includes(grade.id)
			),
		[availableGradeOptions, selectedGrades]
	);

	if (loading) {
		return (
			<Box sx={{ p: 3 }}>
				<Skeleton variant="text" width={200} height={40} />
				<Skeleton variant="text" width="60%" />
				<Grid container spacing={2} sx={{ mt: 2 }}>
					<Grid item xs={12}>
						<Skeleton variant="circular" width={100} height={100} />
					</Grid>
					<Grid item xs={12}>
						<Skeleton variant="rectangular" height={400} />
					</Grid>
				</Grid>
			</Box>
		);
	}

	// Function to get school name by ID
	const getSchoolName = (schoolId) => {
		const school = schools.find((s) => s.id === schoolId);
		return school ? school.name : "Unknown School";
	};

	return (
		<Box sx={{ p: 3 }}>
			<Typography
				variant="h5"
				sx={{ mb: 3, fontWeight: 600, color: "#111827" }}>
				Account Settings
			</Typography>

			{message.text && (
				<Alert severity={message.type} sx={{ mb: 3 }}>
					{message.text}
				</Alert>
			)}

			<Card>
				<CardContent>
					<form onSubmit={handleSubmit}>
						<Grid container spacing={3}>
							{/* Avatar Upload */}
							<Grid item xs={12}>
								<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
									<Box sx={{ position: "relative", mr: 2 }}>
										<Avatar
											src={profile.avatar_url || undefined}
											alt={profile.full_name || "Profile"}
											sx={{
												width: 100,
												height: 100,
												fontSize: "2rem", // Size for fallback initials
											}}>
											{/* Show initials if no image */}
											{!profile.avatar_url && profile.full_name
												? profile.full_name
														.split(" ")
														.map((n) => n[0])
														.join("")
														.toUpperCase()
												: undefined}
										</Avatar>
										{uploading && (
											<Box
												sx={{
													position: "absolute",
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													bgcolor: "rgba(0, 0, 0, 0.5)",
													borderRadius: "50%",
												}}>
												<CircularProgress size={40} />
											</Box>
										)}
									</Box>
									<Button
										component="label"
										variant="outlined"
										startIcon={<Camera size={18} />}
										sx={{ mr: 2 }}
										disabled={uploading}>
										{uploading ? "Uploading..." : "Upload Photo"}
										<input
											type="file"
											hidden
											accept="image/*"
											onChange={handleAvatarUpload}
											disabled={uploading}
										/>
									</Button>
								</Box>
							</Grid>

							{/* Personal Information */}
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Full Name"
									value={profile.full_name}
									onChange={(e) =>
										setProfile((prev) => ({
											...prev,
											full_name: e.target.value,
										}))
									}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="WhatsApp Number"
									value={profile.phone_number}
									onChange={(e) =>
										setProfile((prev) => ({
											...prev,
											phone_number: e.target.value,
										}))
									}
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Email"
									value={profile.email}
									InputProps={{
										readOnly: true,
									}}
									sx={{
										"& .MuiInputBase-input.Mui-disabled": {
											WebkitTextFillColor: "#000000",
										},
									}}
								/>
							</Grid>

							{/* School Selection - Now non-editable after first save */}
							<Grid item xs={12} md={6}>
								{schoolSaved ? (
									// Render as read-only field if school is already saved
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<TextField
											fullWidth
											label="School"
											value={getSchoolName(profile.school_id)}
											InputProps={{
												readOnly: true,
											}}
											sx={{
												"& .MuiInputBase-input.Mui-disabled": {
													WebkitTextFillColor: "#000000",
												},
											}}
										/>
										<Tooltip
											title="School cannot be changed once assigned"
											placement="right">
											<Box
												sx={{ ml: 1, display: "flex", alignItems: "center" }}>
												<Info size={18} color="#6366f1" />
											</Box>
										</Tooltip>
									</Box>
								) : (
									// Render as dropdown if school is not yet saved
									<FormControl fullWidth>
										<InputLabel>School</InputLabel>
										<Select
											value={profile.school_id}
											label="School"
											onChange={(e) => handleSchoolChange(e.target.value)}>
											{schools.map((school) => (
												<MenuItem key={school.id} value={school.id}>
													{school.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								)}
							</Grid>

							{/* Grade Selection - searchable multi-select */}
							<Grid item xs={12} md={6}>
								<Autocomplete
									multiple
									options={availableGradeOptions}
									value={selectedGradeOptions}
									onChange={(event, newValue) => {
										setSelectedGrades(newValue.map((grade) => grade.id));
									}}
									getOptionLabel={(option) => option.name}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									disabled={!profile.school_id}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											label="Grades"
											placeholder={
												profile.school_id ? "Search grades..." : ""
											}
										/>
									)}
								/>
							</Grid>

							{/* Submit Button */}
							<Grid item xs={12}>
								<Stack direction="row" spacing={2} justifyContent="flex-end">
									<Button
										type="submit"
										variant="contained"
										disabled={saving}
										sx={{
											backgroundColor: "#6366f1",
											"&:hover": { backgroundColor: "#4f46e5" },
										}}>
										{saving ? "Saving..." : "Save Changes"}
									</Button>
								</Stack>
							</Grid>
						</Grid>
					</form>
				</CardContent>
			</Card>
		</Box>
	);
};

export default Account;
